import {SignalData} from '@domain/kiln-monitoring'
import {ChartData, ChartDataset, ScriptableLineSegmentContext} from 'chart.js'
import moment from 'moment'

import {SignalGraphData} from '../../types/sensor.types'
import {roundValue} from '../../utils/sensor.utils'

const findNearestNonNull = (
  arr: (number | null | undefined)[],
  index: number,
  start: number,
  end: number,
  spanGaps: boolean
) => {
  const len = spanGaps ? arr.length : 1

  if (start > -1 && end > -1) {
    if (arr[index] !== null) return arr[index]
    for (let offset = 1; offset < len; offset++) {
      const left = index - offset
      const right = index + offset

      if (right < len && arr[right] !== null && index < end && index > start) return arr[right]
      if (left >= 0 && arr[left] !== null && index >= start && index <= end) return arr[left]
    }
  }

  return null // no non-null elements found
}

const findStartEnd = (arr: (number | null | undefined)[]) => {
  const len = arr.length
  let start = -1
  let end = -1

  for (let i = 1; i < len; i++) {
    if (arr[i] !== null) {
      if (start === -1) {
        start = i
      } else {
        end = i
      }
    }
  }

  return {start, end} // no non-null elements found
}

export const computeLineColor = (
  avgValues: number[],
  modelMin: (number | null | undefined)[] | undefined,
  modelMax: (number | null | undefined)[] | undefined,
  spanGaps: boolean
) => ({
  borderColor: (ctx: ScriptableLineSegmentContext) => {
    if (modelMin && modelMax && modelMin.length && modelMax.length) {
      const {start, end} = findStartEnd(modelMin)
      const i = ctx.p0DataIndex
      const y0 = avgValues[i]
      const y1 = avgValues[i + 1]
      const min = findNearestNonNull(modelMin, i, start, end, spanGaps) ?? 0
      const max = findNearestNonNull(modelMax, i, start, end, spanGaps) ?? 1000000
      const minNext = findNearestNonNull(modelMin, i + 1, start, end, spanGaps) ?? 0
      const maxNext = findNearestNonNull(modelMax, i + 1, start, end, spanGaps) ?? 1000000

      const outOfBounds0 = y0 < min || y0 > max
      const outOfBounds1 = y1 < minNext || y1 > maxNext

      return outOfBounds0 || outOfBounds1 ? 'red' : '#016AD4'
    }

    return '#016AD4'
  }
})

export type TCustomChartData = {
  labels: string[]
  datasets: {
    pointRadius: number
    pointBorderWidth: number
    pointHoverRadius: number
    label: string
    data: number[]
    borderWidth: number
    unit: string
    values: number[]
    borderColor: string
    backgroundColor: string
    spanGaps: boolean
  }[]
}
const COLORS = [
  '#016AD4',
  '#D15C02',
  '#009D93',
  '#F9DC5C',
  '#E36588',
  '#42F2F7',
  '#5F0F40',
  '#01D402',
  '#D401D3'
]

function* chartColorGenerator() {
  for (const color of COLORS) {
    yield color
  }
}

type LabelsOverride = {
  avgValues?: string
  maxValues?: string
  minValues?: string
  modelMean?: string
  modelMin?: string
  modelMax?: string
}

const getPointProps = (firstNValues: number[]) => {
  const nonNullValues = firstNValues.filter((value) => value !== null).length
  return {
    pointRadius: nonNullValues < 6 ? 2 : 0,
    pointBorderWidth: nonNullValues < 6 ? 2 : 0,
    pointHoverRadius: nonNullValues < 6 ? 3 : 2
  }
}

export const calcSpanGaps = (bucketSize?: string) => {
  const duration = moment.duration(bucketSize ?? '00:00:00')
  return duration.asSeconds() <= 3600
}

export const getSingleSignalData = ({
  data,
  labelsOverride
}: {
  data?: SignalGraphData
  labelsOverride?: LabelsOverride
}): ChartData & {bucketSize?: string} => {
  const spanGaps = calcSpanGaps(data?.bucketSize)
  if (!data?.data?.length) return {labels: [], datasets: []}
  const signalData = data.data[0]
  const datasets: ChartDataset[] = [
    {
      label: labelsOverride?.avgValues || 'Signal',
      data: signalData.values.avgValues,
      borderWidth: 2,
      borderColor: '#016AD4',
      backgroundColor: '#016AD4',
      spanGaps: spanGaps,
      ...getPointProps(signalData.values.avgValues.slice(0, 10)),
      segment: {
        ...computeLineColor(
          signalData.values.avgValues,
          signalData.values.modelMin,
          signalData.values.modelMax,
          spanGaps
        )
      }
    }
  ]

  if (signalData.values?.maxValues?.length) {
    const color = '#E36588'
    datasets.push({
      label: labelsOverride?.maxValues || 'Signal Max',
      data: signalData.values.maxValues,
      pointRadius: 0,
      pointBorderWidth: 0,
      pointHoverRadius: 2,
      borderWidth: 2,
      spanGaps: spanGaps,
      borderColor: color,
      backgroundColor: color,
      hidden: true
    })
  }

  if (signalData.values?.minValues?.length) {
    const color = '#01D402'
    datasets.push({
      label: labelsOverride?.minValues || 'Signal Min',
      data: signalData.values.minValues,
      pointRadius: 0,
      pointBorderWidth: 0,
      pointHoverRadius: 2,
      borderWidth: 2,
      spanGaps: spanGaps,
      borderColor: color,
      backgroundColor: color,
      hidden: true
    })
  }

  if (signalData.values?.modelMean?.length) {
    datasets.push({
      label: labelsOverride?.modelMean || 'Model',
      data: signalData.values.modelMean,
      pointRadius: 0,
      pointBorderWidth: 0,
      pointHoverRadius: 0,
      borderWidth: 2,
      borderColor: '#8C3CC9',
      backgroundColor: '#8C3CC9',
      spanGaps: spanGaps
    })
  }

  if (signalData.values?.modelMax?.length && signalData.values?.modelMin?.length) {
    datasets.push({
      label: labelsOverride?.modelMin || 'modelMin',
      data: signalData.values.modelMin,
      pointBorderWidth: 0,
      pointHoverRadius: 0,
      borderWidth: 1,
      pointRadius: 0,
      showLine: false,
      spanGaps: spanGaps
    })
    datasets.push({
      label: labelsOverride?.modelMax || 'modelMax',
      data: signalData.values.modelMax,
      pointBorderWidth: 0,
      pointHoverRadius: 0,
      borderWidth: 1,
      pointRadius: 0,
      fill: '-1',
      showLine: false,
      backgroundColor: 'rgba(140,60,201,0.1)',
      spanGaps: spanGaps
    })
  }

  return {
    labels: data?.labels ? data.labels : [],
    datasets: datasets ? datasets : [],
    bucketSize: data.bucketSize
  }
}

export const getMultiSignalYAxes = (
  chartData: TCustomChartData,
  scaleToShowThreshold?: boolean
) => {
  const units: Record<string, {min: number; max: number}> = {}
  chartData.datasets.forEach((item) => {
    if (units[item.unit]) {
      const minVal = Math.min(...item.values)
      const maxVal = Math.max(...item.values)
      units[item.unit] = {
        min: units[item.unit].min < minVal ? units[item.unit].min : minVal,
        max: units[item.unit].max > maxVal ? units[item.unit].max : maxVal
      }
    } else {
      const minVal = Math.min(...item.values)
      const maxVal = Math.max(...item.values)
      units[item.unit] = {
        min: minVal,
        max: maxVal
      }
    }
  })

  const yAxes = {}
  const colorGenerator = chartColorGenerator()

  Object.keys(units).forEach((key) => {
    const axeName = `y_${key}`
    const color =
      colorGenerator.next().value ?? `#${Math.floor(Math.random() * 16777215).toString(16)}`

    const min = units[key].min
    const max = units[key].max
    const buffer = (max - min) * 0.2

    yAxes[axeName] = {
      suggestedMax: scaleToShowThreshold ? max + buffer : max,
      suggestedMin: scaleToShowThreshold ? min - buffer : min,
      grid: {
        display: false
      },
      border: {
        display: false
      },
      ticks: {
        color: color,
        padding: 2,
        callback: function (value) {
          const parsedValue = typeof value === 'string' ? value : roundValue(value)
          return `${parsedValue} ${key}`
        }
      }
    }
  })

  return yAxes
}

export const getMultiSignalData = (signals?: SignalData[], data?: SignalGraphData) => {
  const colorGenerator = chartColorGenerator()
  const datasets = data?.data?.map((signalData) => {
    const signal = signals?.find((signal) => signal.localName === signalData.localName)
    const color =
      colorGenerator.next().value ?? `#${Math.floor(Math.random() * 16777215).toString(16)}`
    return {
      label: signal?.description ?? '',
      data: signalData.values.avgValues ?? [],
      borderWidth: 2,
      unit: signal?.unit ?? '',
      values: signalData.values.avgValues,
      borderColor: color,
      backgroundColor: color,
      spanGaps: true,
      yAxisID: `y_${signal?.unit}`,
      ...getPointProps(signalData?.values?.avgValues?.slice(0, 10) ?? [])
    }
  })

  return {
    labels: data?.labels ? data.labels : [],
    datasets: datasets ? datasets : []
  }
}
